import { setRenderedAppState } from 'src/modules/App/nonPersistentActions'
import { resetClaims } from 'src/modules/Claims/actions'
import { clearDocuments } from 'src/modules/Documents/actions'
import { resetPaymentsState } from 'src/modules/Payments/actions'
import { resetChat } from 'src/modules/Chat/actions'
import {
  getBrandSettings,
  getFeatures,
  isEmpty,
  logError,
  updateDocumentTitle,
} from 'src/utils/helpers'
import { checkIfChatOnly } from 'src/utils/features'

const KIOSK_USER_LOGIN_EVENT = 'logInAsKioskUserLink'

/**
 * Reset global app redux states and local storage.
 *
 * @param onEvent When is app restarted.
 * <ul>
 *   <li>"logInPin" - When logging in with PIN</li>
 *   <li>"logInAsKioskUserLink" - When using "Login as Kiosk user"</li>
 *   <li>"logInFromEmailLink" - When using a link from automated or manual emails</li>
 *   <li>"logInTokenLink" - When logging in with a link containing the token (more as a fallback)</li>
 *   <li>"logOut" - When logging out</li>
 * </ul>
 * @param isWidget
 * @param history Provided using "withRouter" from 'react-router-dom'
 */
// eslint-disable-next-line no-unused-vars
export function resetApp(onEvent = '', isWidget = false, history = null, userChanged = false) {
  return dispatch => {
    // TODO: Check if this will only restart storage, but not the redux state
    dispatch({
      type: 'RESET_APP',
    })

    // Also clear documents from state
    dispatch(clearDocuments())

    // Clear payments state
    dispatch(resetPaymentsState())

    // Reset document title
    updateDocumentTitle()
    const newUserLoggedIn = onEvent === KIOSK_USER_LOGIN_EVENT && userChanged
    if (onEvent === 'logOut' || newUserLoggedIn) {
      // Reset claims form
      dispatch(resetClaims())

      // Clear chat state (re-render is not needed because we'll redirect to home below)
      dispatch(resetChat(newUserLoggedIn))

      // Restart language
      dispatch(resetLanguage())

      if (history) {
        history.push('/')
      }
    }
  }
}

export function appUpdated() {
  return { type: 'APP_UPDATED' }
}

export function clearAppCache() {
  return { type: 'CLEAR_APP_CACHE' }
}

export function setChat(chat) {
  return { type: 'SET_CHAT', payload: { chat } }
}

export function setChatInit(chatInit = true) {
  return { type: 'SET_CHAT_INIT', payload: chatInit }
}

export function setFeatures(data) {
  if (isEmpty(data)) {
    return {}
  }

  return async dispatch => {
    dispatch({
      type: 'SET_FEATURES',
      payload: {
        features: data.features,
      },
    })

    try {
      dispatch(setChat(data.chat))
    } catch (e) {
      logError('Could not set chat from response')
    }

    try {
      dispatch(setIsChatOnlyFeature(checkIfChatOnly(data)))
    } catch (e) {
      logError('Could not find features from settings')
    }
  }
}

export function setSettings(settings) {
  if (typeof settings !== 'object' || isEmpty(settings) || settings.hasOwnProperty('error')) {
    return false
  }

  return async dispatch => {
    dispatch({
      type: 'SET_SETTINGS',
      payload: {
        settings: settings,
      },
    })
  }
}

export function setSendingData(sending) {
  return {
    type: 'SET_SENDING_DATA',
    payload: { sending },
  }
}

/**
 * Changes the language of Kiosk App.
 * If needed, it also requests new settings from backend to get those translations as well.
 *
 * @param {string} language Actual two letter language two set
 * @param {boolean} fetch Should we fetch new settings from backend (for getting custom texts in other languages)
 * @param {string} onEvent When is this called:
 * <ul>
 *   <li>"selected" - When user manually changes it to specific language</li>
 *   <li>"authRequest" - When PIN is requested and we found contact's language</li>
 *   <li>"onRender" - When LanguageWrapper -> componentDidMount runs</li>
 *   <li>"widget" - When defined with custom widget param</li>
 * </ul>
 * @return {function(...[*]=)}
 */
export function setLanguage(language, fetch = true, onEvent = 'selected') {
  return async dispatch => {
    // Set main App component as not rendered so it can re-render on language change
    dispatch(setRenderedAppState(false))

    dispatch({
      type: 'SET_LANGUAGE',
      payload: {
        language,
        fetch,
        onEvent,
      },
    })

    if (fetch) {
      const settings = await getBrandSettings('i-a')
      const data = await getFeatures()
      try {
        dispatch(setSettings(settings))
        dispatch(setFeatures(data))
      } catch (e) {
        logError('Dispatch set settings after setLanguage', e)
      }
    }
  }
}

export function resetLanguage(fetch = true) {
  return async dispatch => {
    dispatch({
      type: 'RESET_LANGUAGE',
    })

    if (fetch) {
      const settings = await getBrandSettings('i-a')
      const data = await getFeatures()

      try {
        dispatch(setSettings(settings))
        dispatch(setFeatures(data))
      } catch (e) {
        logError('Dispatch set settings after resetLanguage', e)
      }
    }

    // Set main App component as not rendered so it can re-render on language change
    dispatch(setRenderedAppState(false))
  }
}

export function setCurrentlyOpenFeature(feature) {
  return dispatch => {
    dispatch({
      type: 'SET_CURRENTLY_OPEN_FEATURE',
      payload: {
        feature,
      },
    })
  }
}

export function closeSidebar() {
  return dispatch => {
    dispatch({
      type: 'CLOSE_SIDEBAR',
    })
  }
}

export function setNotificationBannerHidden(hidden) {
  return dispatch => {
    dispatch({
      type: 'SET_NOTIFICATION_BANNER_HIDDEN',
      payload: {
        hidden,
      },
    })
  }
}

export function setNotificationHeight(style) {
  return dispatch => {
    dispatch({
      type: 'SET_NOTIFICATION_HEIGHT',
      payload: {
        style,
      },
    })
  }
}

export function openSidebar() {
  return dispatch => {
    dispatch({
      type: 'OPEN_SIDEBAR',
    })
  }
}

export function afterAddToHomeScreenClose() {
  return dispatch => {
    dispatch({
      type: 'AFTER_ADD_TO_HOME_SCREEN_CLOSE',
    })
  }
}

export function setIsChatOnlyFeature(isChatOnlyFeature) {
  return {
    type: 'SET_IS_CHAT_ONLY_FEATURE',
    payload: { isChatOnlyFeature },
  }
}

export function setIsIdle(isIdle) {
  return {
    type: 'SET_IS_IDLE',
    payload: { isIdle },
  }
}

export function setIsWidget(isWidget) {
  return dispatch => {
    dispatch({
      type: 'SET_IS_WIDGET',
      payload: {
        isWidget,
      },
    })
  }
}

export function setIsAccessibe(isAccessibe) {
  return dispatch => {
    dispatch({
      type: 'SET_IS_ACCESSIBE',
      payload: {
        isAccessibe,
      },
    })
  }
}

export function setAppLoading(isAppLoading) {
  return dispatch => {
    dispatch({
      type: 'SET_IS_APP_LOADING',
      payload: {
        isAppLoading,
      },
    })
  }
}
